import { createClient, type AuthChangeEvent, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { RESUME_TYPES, resumeExt, type ProfileView, type ResumeExt } from "../../shared/types.ts";
import { getMe, saveProfile, setResume } from "./api.ts";
import { rememberNext } from "./next.ts";

/**
 * Bun's bundler replaces literal `process.env.X` reads with their values from .env when the name matches the build's
 * `env` prefix: build.ts inlines `PUBLIC_SUPABASE_*` and the dev server (bunfig.toml) `PUBLIC_*`, so set PUBLIC_SUPABASE_URL
 * and PUBLIC_SUPABASE_ANON_KEY.
 * SUPABASE_URL and SUPABASE_ANON_KEY are read as a fallback for a build that inlines those names instead.
 * A name that isn't inlined leaves `process` undefined in the browser, so each read is guarded and sign-in shows as
 * unavailable rather than crashing the page.
 */
function env(read: () => string | undefined): string {
  try {
    return read() ?? "";
  } catch {
    return "";
  }
}

const URL_ = env(() => process.env.PUBLIC_SUPABASE_URL) || env(() => process.env.SUPABASE_URL);
const KEY = env(() => process.env.PUBLIC_SUPABASE_ANON_KEY) || env(() => process.env.SUPABASE_ANON_KEY);

/** Whether the page is served from this machine, the only place demo mode may run. */
function loopback(): boolean {
  try {
    return ["localhost", "127.0.0.1", "[::1]"].includes(location.hostname);
  } catch {
    return false;
  }
}

/**
 * Development only: with PUBLIC_DEMO_TOKEN set when running `bun web/dev.ts`, the app signs in as a local demo user whose
 * bearer token is that value, with no Supabase project. Three locks keep it out of production: build.ts blanks the
 * name and inlines only `PUBLIC_SUPABASE_*`, a production build skips the read, and the page must be on a loopback host. The Worker never accepts the token.
 */
const DEMO_TOKEN = env(() => (process.env.NODE_ENV === "production" ? "" : process.env.PUBLIC_DEMO_TOKEN));
const DEMO = DEMO_TOKEN && loopback() ? DEMO_TOKEN : "";
const DEMO_OUT = "headcount.demo.signed-out";
const demoListeners = new Set<(event: AuthChangeEvent, session: Session | null) => void>();

function demoSession(): Session | null {
  try {
    if (localStorage.getItem(DEMO_OUT) === "1") return null;
  } catch {}
  return { access_token: DEMO, token_type: "bearer", expires_in: 3600, refresh_token: "", user: { id: "demo", email: "demo@headcount.app" } } as unknown as Session;
}

function demoSet(signedIn: boolean): Session | null {
  try {
    if (signedIn) localStorage.removeItem(DEMO_OUT);
    else localStorage.setItem(DEMO_OUT, "1");
  } catch {}
  const s = demoSession();
  for (const l of demoListeners) l(signedIn ? "SIGNED_IN" : "SIGNED_OUT", s);
  return s;
}

export const configured = DEMO !== "" || (URL_ !== "" && KEY !== "");

let client: SupabaseClient | null = null;

function supabase(): SupabaseClient {
  if (!configured) throw new Error("not_configured");
  client ??= createClient(URL_, KEY, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: "pkce" } });
  return client;
}

export class AuthFailure extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

const fail = (e: { code?: string; message: string; status?: number }): never => {
  throw new AuthFailure(e.code ?? e.message, e.status ?? 400);
};

/** Creates the account. `confirm` is true when the project asks for email confirmation before the first session. */
export async function signUp(email: string, password: string, next?: string | null): Promise<{ session: Session | null; confirm: boolean }> {
  if (DEMO) return { session: demoSet(true), confirm: false };
  const { data, error } = await supabase().auth.signUp({ email, password, options: { emailRedirectTo: `${location.origin}${next ?? "/profile"}` } });
  if (error) fail(error);
  return { session: data.session, confirm: !data.session };
}

export async function logIn(email: string, password: string): Promise<Session> {
  if (DEMO) return demoSet(true)!;
  const { data, error } = await supabase().auth.signInWithPassword({ email, password });
  if (error) fail(error);
  return data.session!;
}

/**
 * Leaves for Google and comes back to this origin, where supabase-js picks the session up from the URL. It resolves as soon
 * as the browser starts leaving, so a user who backs out of Google lands on the page as the back-forward cache froze it.
 * `next`, a same-origin path, is remembered for the app to open once the session is in, since Google returns to the root.
 */
export async function logInWithGoogle(next?: string | null): Promise<void> {
  if (DEMO) return void demoSet(true);
  rememberNext(next ?? null);
  const { error } = await supabase().auth.signInWithOAuth({ provider: "google", options: { redirectTo: location.origin } });
  if (error) {
    rememberNext(null);
    fail(error);
  }
}

/**
 * Sends the reset email. Supabase answers the same whether or not the email has an account, so the screen never says
 * which. The link opens /reset-password, which must be on the project's redirect allow list.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  if (DEMO) return;
  const { error } = await supabase().auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/reset-password` });
  if (error) fail(error);
}

/**
 * Signs in with the reset email's token hash. Called only when the new password is submitted, so a mail scanner that
 * loads the link can't use the token up, and it needs no code verifier, so the link works on any device.
 */
export async function verifyRecovery(tokenHash: string): Promise<void> {
  if (DEMO) return void demoSet(true);
  const { error } = await supabase().auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
  if (error) fail(error);
}

/**
 * Sets the signed-in user's password, as the reset screen does once the recovery link signed them in, then ends every
 * other session, so whoever else held the account loses it once their access token expires.
 */
export async function setPassword(password: string): Promise<void> {
  if (DEMO) return;
  const { error } = await supabase().auth.updateUser({ password });
  if (error) fail(error);
  await supabase().auth.signOut({ scope: "others" });
}

export async function logOut(): Promise<void> {
  if (DEMO) return void demoSet(false);
  if (!configured) return;
  await supabase().auth.signOut();
}

export async function getSession(): Promise<Session | null> {
  if (DEMO) return demoSession();
  if (!configured) return null;
  const { data } = await supabase().auth.getSession();
  return data.session;
}

/** The current access token for the API's bearer header; supabase-js refreshes it before it expires. */
export async function accessToken(): Promise<string | null> {
  return (await getSession())?.access_token ?? null;
}

export function onAuthStateChange(fn: (event: AuthChangeEvent, session: Session | null) => void): () => void {
  if (DEMO) {
    demoListeners.add(fn);
    return () => demoListeners.delete(fn);
  }
  if (!configured) return () => {};
  const { data } = supabase().auth.onAuthStateChange(fn);
  return () => data.subscription.unsubscribe();
}

/**
 * Puts the file at `resumes/{user id}/resume.{ext}`, replacing the last one, then tells the API where it is. The file is
 * re-typed from its extension: supabase-js sends a File's own type, the browser's guess, which is empty or differs by OS
 * for doc, rtf and md, and the bucket rejects any type outside its list. The file's own name goes on the profile, for the
 * app to show and the engine to send it under. The local demo stores nothing and only records the path and name.
 */
export async function uploadResume(file: File): Promise<ProfileView> {
  const ext = resumeExt(file.name);
  if (!ext) throw new AuthFailure("bad_type", 400);
  const path = DEMO ? `${(await getMe()).user.id}/resume.${ext}` : await storeResume(file, ext);
  // The name is saved before the path, so the profile never shows this file under the last upload's name.
  await saveProfile({ resumeFile: file.name.slice(-255) });
  return setResume(path);
}

async function storeResume(file: File, ext: ResumeExt): Promise<string> {
  const session = await getSession();
  if (!session) throw new AuthFailure("signed_out", 401);
  const path = `${session.user.id}/resume.${ext}`;
  const { error } = await supabase()
    .storage.from("resumes")
    .upload(path, new File([file], `resume.${ext}`, { type: RESUME_TYPES[ext] }), { upsert: true, contentType: RESUME_TYPES[ext] });
  if (error) throw new AuthFailure(error.message, 400);
  return path;
}

/**
 * Puts an intern application's resume at `resumes/{user id}/careers/{role}.pdf`, replacing the last one: `file` when the
 * applicant picked one, otherwise a copy of the PDF resume on their profile, so the application keeps its own file when the
 * profile's changes. The local demo stores nothing.
 */
export async function storeCareerResume(role: string, file: File | null): Promise<void> {
  if (DEMO) return;
  const session = await getSession();
  if (!session) throw new AuthFailure("signed_out", 401);
  const bucket = supabase().storage.from("resumes");
  let body: Blob | null = file;
  if (!body) {
    const { data, error } = await bucket.download(`${session.user.id}/resume.pdf`);
    if (error) throw new AuthFailure(error.message, 400);
    body = data;
  }
  const { error } = await bucket.upload(`${session.user.id}/careers/${role}.pdf`, new File([body], `${role}.pdf`, { type: RESUME_TYPES.pdf }), {
    upsert: true,
    contentType: RESUME_TYPES.pdf,
  });
  if (error) throw new AuthFailure(error.message, 400);
}
